import { type I18n } from "@lingui/core";
import { matchPath } from "react-router-dom";

import { PermissionBit, UserFeatureFlag } from "@/api";
import { clinicalStudies } from "@/constants/enum-to-text";
import {
  hasAdminAccess,
  hasClinicalStudiesAccess,
  hasMonitoringAccess,
  hasPermission,
} from "@/constants/permissions";

import { AppModel } from "../../app-model";
import { pageTitles } from "../../page-titles";
import { isUnder, MenuItem } from "./menu-list";

/**
 * Single source of truth for navigation: the whole tree is derived from the app
 * state and the current URL, so a section like Admin can be browsed from
 * anywhere. Sections tied to the entity you're viewing (the current patient or
 * study) are filled in only on that route.
 *
 * Rebuilt on every render, so labels stay current as long as the caller's
 * `i18n` comes from `useLingui()` (which re-renders on locale change).
 */
export function buildMenu(
  app: AppModel,
  pathname: string,
  i18n: I18n,
): MenuItem[] {
  const permissions = app.seerlinqApi.permissions;
  const monitoring = hasMonitoringAccess(permissions);
  const admin = hasAdminAccess(permissions);
  const studies = hasPermission(permissions, PermissionBit.CLINICAL_STUDIES);

  // `app.patient` is the single source for the current patient (id, name,
  // enrollments). matchPath only gates it to patient routes, since app.patient
  // lingers after you navigate away.
  const patientId = matchPath({ path: "/patient", end: false }, pathname)
    ? (app.patient?.patientId ?? null)
    : null;
  const study =
    matchPath({ path: "/clinical-studies/:name", end: false }, pathname)?.params
      .name ?? null;

  return [
    {
      to: "/",
      label: i18n._(pageTitles.listOfPatients),
      // The current patient nests here, since there's no main item for one.
      children:
        patientId === null
          ? undefined
          : [
              {
                to: `/patient/${patientId}`,
                label: app.patient?.data?.full_name ?? `#${patientId}`,
                children: [
                  {
                    to: `/patient/${patientId}`,
                    label: i18n._(pageTitles.trends),
                  },
                  {
                    to: `/patient/${patientId}/physicians`,
                    label: i18n._(pageTitles.physicians),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/alerts`,
                    label: i18n._(pageTitles.alerts),
                    show: hasPermission(permissions, PermissionBit.VIEW_ALERTS),
                    children: [
                      {
                        to: `/patient/${patientId}/alerts/thresholds`,
                        label: i18n._(pageTitles.thresholds),
                        show: hasPermission(
                          permissions,
                          PermissionBit.VIEW_THRESHOLDS,
                        ),
                      },
                    ],
                  },
                  {
                    to: `/patient/${patientId}/lvfp`,
                    label: i18n._(pageTitles.lvfp),
                    show: hasPermission(permissions, PermissionBit.ACCESS_LVFP),
                  },
                  {
                    to: `/patient/${patientId}/dri`,
                    label: i18n._(pageTitles.dri),
                    show: hasPermission(permissions, PermissionBit.ACCESS_DRI),
                  },
                  {
                    to: `/patient/${patientId}/afib`,
                    label: i18n._(pageTitles.afib),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/vitals`,
                    label: i18n._(pageTitles.vitals),
                  },
                  {
                    to: `/patient/${patientId}/symptoms`,
                    label: i18n._(pageTitles.symptoms),
                  },
                  {
                    to: `/patient/${patientId}/events`,
                    label: i18n._(pageTitles.events),
                  },
                  {
                    to: `/patient/${patientId}/medications`,
                    label: i18n._(pageTitles.medications),
                  },
                  {
                    to: `/patient/${patientId}/diags`,
                    label: i18n._(pageTitles.comorbidities),
                  },
                  {
                    to: `/patient/${patientId}/labs`,
                    label: i18n._(pageTitles.labs),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/exams`,
                    label: i18n._(pageTitles.exams),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/basics`,
                    label: i18n._(pageTitles.basicMedicalData),
                  },
                  {
                    to: `/patient/${patientId}/ppg`,
                    label: i18n._(pageTitles.ppgData),
                  },
                  {
                    to: `/patient/${patientId}/ecg`,
                    label: i18n._(pageTitles.ecgData),
                  },
                  {
                    to: `/patient/${patientId}/any`,
                    label: i18n._(pageTitles.addAnyData),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/healthpro`,
                    label: i18n._(pageTitles.healthProReporting),
                    show: monitoring,
                  },
                  {
                    to: `/patient/${patientId}/reports`,
                    label: i18n._(pageTitles.medicalReports),
                  },
                  {
                    to: `/patient/${patientId}/logs`,
                    label: i18n._(pageTitles.logs),
                    show: admin,
                  },
                  {
                    to: `/patient/${patientId}/questionnaire`,
                    label: i18n._(pageTitles.questionnaires),
                  },
                  {
                    to: `/patient/${patientId}/clinical-studies`,
                    label: i18n._(pageTitles.clinicalStudies),
                    show: hasClinicalStudiesAccess(permissions),
                    children: (app.patient?.enrollments ?? []).map((e) => ({
                      to: `/patient/${patientId}/clinical-studies/${e.study_acronym}`,
                      label: clinicalStudies[e.study_acronym],
                    })),
                  },
                ],
              },
            ],
    },
    {
      to: "/add-patient",
      label: i18n._(pageTitles.addNewPatient),
      show: app.seerlinqApi.canIAddPatients,
    },
    {
      to: "/med-waiting-room",
      label: i18n._(pageTitles.medWaitingRoom),
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    {
      to: "/tech-waiting-room",
      label: i18n._(pageTitles.techWaitingRoom),
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    {
      to: "/audit-room",
      label: i18n._(pageTitles.auditRoom),
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    { to: "/questionnaires", label: i18n._(pageTitles.questionnaires) },
    {
      to: "/daily-monitoring",
      label: i18n._(pageTitles.dailyMonitoring),
      show:
        monitoring &&
        app.seerlinqApi.hasFeatureFlag(
          UserFeatureFlag.fe_daily_monitoring_dashboard,
        ),
    },
    { to: "/calendar", label: i18n._(pageTitles.calendar), show: studies },
    {
      to: "/clinical-studies",
      label: i18n._(pageTitles.clinicalStudies),
      show: studies,
      children:
        study === null
          ? undefined
          : [
              {
                to: `/clinical-studies/${study}`,
                label: i18n._(pageTitles.definition),
              },
              {
                to: `/clinical-studies/${study}/overview`,
                label: i18n._(pageTitles.overview),
              },
            ],
    },
    {
      to: "/admin",
      label: i18n._(pageTitles.admin),
      show: monitoring,
      children: [
        { to: "/admin/clinics", label: i18n._(pageTitles.clinics) },
        { to: "/admin/physicians", label: i18n._(pageTitles.physicians) },
        {
          to: "/admin/users",
          label: i18n._(pageTitles.users),
          show: monitoring,
        },
        {
          to: "/admin/monitoring-management",
          label: i18n._(pageTitles.monitoringManagement),
          show: monitoring,
        },
        {
          to: "/admin/access-policies",
          label: i18n._(pageTitles.accessPolicies),
          show: admin,
        },
        {
          to: "/admin/limiters",
          label: i18n._(pageTitles.accessLimiters),
          show: admin,
        },
        {
          to: "/admin/analytics",
          label: i18n._(pageTitles.analytics),
          show: admin,
        },
        {
          to: "/admin/settings",
          label: i18n._(pageTitles.settings),
          show: admin,
          defaultExpanded: true,
          children: [
            {
              to: "/admin/settings/monitoring",
              label: i18n._(pageTitles.monitoring),
            },
            {
              to: "/admin/settings/questionnaires",
              label: i18n._(pageTitles.questionnaires),
            },
            {
              to: "/admin/settings/consents",
              label: i18n._(pageTitles.consents),
            },
          ],
        },
      ],
    },
  ];
}

/** The submenu of the active top-level section, for the desktop sidebar. */
export function getActiveSubmenu(
  menu: MenuItem[],
  pathname: string,
): MenuItem[] | null {
  const section = menu.find(
    (item) => item.children && isUnder(item.to, pathname),
  );
  if (!section?.children) {
    return null;
  }

  // If the section's submenu is a single active entity wrapper (e.g. the current
  // patient), drop the wrapper and surface its page items directly.
  const [only] = section.children;
  if (
    section.children.length === 1 &&
    only.children?.length &&
    isUnder(only.to, pathname)
  ) {
    return only.children;
  }
  return section.children;
}
